import Fab from '@mui/material/Fab';
import CircularProgress from '@mui/material/CircularProgress';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Tooltip from '@mui/material/Tooltip';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { useTranslation } from 'react-i18next';

import { useNovel } from '@/context/novel-context';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';

interface FloatingSaveButtonProps {
  isSaving: boolean;
  disabled?: boolean;
  label?: string;
  onClick?: () => void;
}

export function FloatingSaveButton({ isSaving, disabled, label, onClick }: FloatingSaveButtonProps) {
  const { t } = useTranslation();
  const { isExampleNovel } = useNovel();
  const { userData } = useUserProfile();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const isReadOnlyExample = isExampleNovel && !userData?.isAdmin;
  const isDisabled = isSaving || disabled || isReadOnlyExample;

  const fab = (
    <Fab
      variant="extended"
      color="primary"
      type={onClick ? 'button' : 'submit'}
      onClick={onClick}
      disabled={isDisabled}
      sx={{
        pointerEvents: 'auto',
        boxShadow: 4,
        transform: 'translateY(-100%)',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      {isSaving ? (
        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
      ) : (
        <SaveIcon sx={{ mr: 1 }} />
      )}
      {label || t('common.save')}
    </Fab>
  );

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: { xs: 24, md: 40 },
        display: 'flex',
        justifyContent: 'flex-end',
        pointerEvents: 'none',
        zIndex: 1000,
        height: 0,
        overflow: 'visible',
        mt: 4,
      }}
    >
      <Fade in={trigger}>
        {isReadOnlyExample ? (
          <Tooltip title={t('example_novel.readonly_hint')} placement="left">
            {/* span needed so Tooltip works on disabled elements */}
            <span style={{ pointerEvents: 'auto', transform: 'translateY(-100%)', display: 'inline-flex' }}>
              {fab}
            </span>
          </Tooltip>
        ) : (
          fab
        )}
      </Fade>
    </Box>
  );
}
